import {useCallback, useEffect, useMemo} from 'react';

import * as SearchDAO from 'search/SearchDAO';
import EntityStore from 'stores/EntityStore';
import useAsyncState, {AsyncStatus} from 'ui-library/hooks/useAsyncState';
import lazyInit from 'util/lazyInit';

import {PromptSuggestion} from './PromptSuggestionTypes';

export type GetSuggestions = () => Promise<Array<PromptSuggestion>>;
export interface RecentFile {
  id: string;
  name: string;
}

/**
 * Shared search results that can be used across multiple suggestion functions.
 * This allows us to fire all searches once and share the results.
 */
export interface SharedSearchResults {
  recentProtein: RecentFile | null;
  recentFolderItem: RecentFile | null;
  recentStudy: RecentFile | null;
  recentProject: RecentFile | null;
  recentEntries: Array<RecentFile>;
}

/**
 * Helper to search for a recently authored file of a given type.
 */
export async function searchForRecentFiles(
  fileType: string,
  numberOfResults: number = 1
): Promise<Array<RecentFile>> {
  const user = EntityStore.currentUser();
  if (user == null) {
    return [];
  }
  const filters = [
    {key: 'TYPES', operator: 'IS_ONE_OF', value: [fileType]},
    {key: 'ARCHIVE_PURPOSES', operator: 'IS_ONE_OF', value: ['NOT_ARCHIVED']},
  ];
  if (fileType !== 'source') {
    filters.push({key: 'AUTHOR', operator: 'IS_ONE_OF', value: [user.handle]});
  }
  const rv = await SearchDAO.postSearchRequest({
    url: '/1/api/search',
    data: {
      filters,
      offset: 0,
      limit: numberOfResults,
      query: '',
      sorts: [{key: 'modified_at', reverse: true}],
      nextToken: null,
      groupBy: null,
      searchSource: 'llm:PROMPT_SUGGESTIONS',
    },
  });
  if (rv.ids.length === 0) {
    return [];
  }

  return rv.ids.map((id: string) => {
    if (fileType === 'entry') {
      const entry = rv.entrySummariesById[id];
      return {id, name: entry.humanId ?? entry.name};
    } else if (fileType === 'source') {
      const project = rv.projectsById[id];
      return {id, name: project.name};
    } else {
      // Assumes this is a bioentity for now
      return {id, name: rv.fileSummariesById[id].name};
    }
  });
}

export async function searchForRecentFile(fileType: string): Promise<RecentFile | null> {
  const results = await searchForRecentFiles(fileType, 1);
  return results.length > 0 ? results[0] : null;
}

const getPromptSuggestionCache = lazyInit(() => new Map<string, Array<PromptSuggestion>>());

/**
 * Wrapper around a `GetSuggestions` function that caches the suggestions for the duration of the session.
 *
 * This avoids flashing to a loading state only to show the same suggestion again when re-mounting the
 * welcome screen of the AI sidepanel.
 *
 * @param id a unique identifier for the suggestion, for caching.
 */
export function useCachedResponse(id: string, getSuggestions: GetSuggestions): GetSuggestions {
  return useCallback(async () => {
    const suggestionCache = getPromptSuggestionCache();
    let suggestions = suggestionCache.get(id);
    if (suggestions == null) {
      suggestions = await getSuggestions();
      suggestionCache.set(id, suggestions);
    }
    return suggestions;
  }, [id, getSuggestions]);
}

/**
 * Wraps an async function that gets prompt suggestions, and returns a list of suggestions.
 *
 * @param getSuggestions should be a referentially stable function to avoid re-querying the suggestions on
 *   every render, i.e. avoid inline functions without useCallback.
 *
 * @returns an array of prompt suggestions, or a null value if the suggestions are still loading.
 */
export function useGetSuggestions<T>(getSuggestions: GetSuggestions): Array<PromptSuggestion | null> {
  const {makeCall: getData, currentState: state} = useAsyncState({makeCall: getSuggestions});
  useEffect(() => {
    getData();
  }, [getData]);

  const suggestions = useMemo(() => {
    switch (state.status) {
      case AsyncStatus.SUCCESS:
        return state.data;
      default:
        // Loading.
        return [null];
    }
  }, [state]);
  return suggestions;
}

const sharedSearchResultsCache = lazyInit(() => new Map<string, SharedSearchResults>());

/**
 * Hook to fetch all search results once and share them across all suggestion functions.
 * This fires all searches in parallel at the start and caches the results for the session.
 *
 * @returns SharedSearchResults or null if still loading
 */
export function useSharedSearchResults(): SharedSearchResults | null {
  const cacheKey = 'shared-search-results';

  const fetchAllSearches = useCallback(async (): Promise<SharedSearchResults> => {
    const cache = sharedSearchResultsCache();
    const cached = cache.get(cacheKey);
    if (cached != null) {
      return cached;
    }

    // Fire all searches in parallel
    const folderItem = searchForRecentFile('basic_folder_item').catch(() => null);
    const proteinRv = searchForRecentFile('protein').catch(() => null);
    const studyRv = searchForRecentFile('study').catch(() => null);
    const sourceRv = searchForRecentFile('source').catch(() => null);
    const entriesRv = searchForRecentFiles('entry', 5).catch(() => []);
    const searchResults: SharedSearchResults = {
      recentFolderItem: await folderItem,
      recentProtein: await proteinRv,
      recentStudy: await studyRv,
      recentProject: await sourceRv,
      recentEntries: await entriesRv,
    };

    cache.set(cacheKey, searchResults);
    return searchResults;
  }, [cacheKey]);

  const {makeCall: getData, currentState: state} = useAsyncState({makeCall: fetchAllSearches});

  useEffect(() => {
    getData();
  }, [getData]);

  return useMemo(() => {
    switch (state.status) {
      case AsyncStatus.SUCCESS:
        return state.data;
      default:
        return null;
    }
  }, [state]);
}
